import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconButton,
    Popover,
    Box,
    Typography,
    useTheme,
    CircularProgress,
} from '@mui/material';
import {
    Apps as AppsIcon,
    TrackChanges,
    ContactsOutlined,
    AssignmentOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import ssoService from '../../services/ssoService';

interface AppItem {
    id: string;
    label: string;
    icon: React.ReactElement;
    path: string;
    color: string;
    external?: boolean;
    visible?: boolean;
}

export const AppLauncher: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { user } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [loadingSso, setLoadingSso] = useState(false);

    // Check if user should see the CRM app
    const isCrmVisible = (): boolean => {
        if (!user) return false;
        if (user.is_super_admin === 1) return true;
        const deptName = (user.department_name || '').toLowerCase();
        return deptName === 'sales' || deptName === 'marketing';
    };

    const appItems: AppItem[] = [
        {
            id: 'okr',
            label: 'OKRs',
            icon: <TrackChanges sx={{ fontSize: 32 }} />,
            path: '/okr',
            color: '#4F46E5',
        },
        {
            id: 'crm',
            label: 'Facilis CRM',
            icon: <ContactsOutlined sx={{ fontSize: 32 }} />,
            path: '', // handled via SSO
            color: '#0072BC',
            external: true,
            visible: isCrmVisible(),
        },
        {
            id: 'mkraftone',
            label: 'MkraftOne',
            icon: <AssignmentOutlined sx={{ fontSize: 32 }} />,
            path: '', // handled via SSO
            color: '#1B3A5C',
            external: true,
        },
    ];

    const visibleApps = appItems.filter((app) => app.visible !== false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAppClick = async (app: AppItem) => {
        if (app.external) {
            try {
                setLoadingSso(true);
                let targetUrl: string;

                if (app.id === 'crm') {
                    const { ssoToken, crmUrl } = await ssoService.generateCrmToken();
                    targetUrl = `${crmUrl}?token=${encodeURIComponent(ssoToken)}`;
                } else if (app.id === 'mkraftone') {
                    const { ssoToken, mkraftoneUrl } = await ssoService.generateMkraftoneToken();
                    targetUrl = `${mkraftoneUrl}?token=${encodeURIComponent(ssoToken)}`;
                } else {
                    return;
                }

                window.open(targetUrl, '_blank');
            } catch (error) {
                console.error('SSO token generation failed:', error);
            } finally {
                setLoadingSso(false);
                handleClose();
            }
        } else {
            navigate(app.path);
            handleClose();
        }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                onClick={handleClick}
                aria-label="App launcher"
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{
                    color: theme.palette.text.primary,
                    width: { xs: 36, sm: 40, md: 44 },
                    height: { xs: 36, sm: 40, md: 44 },
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }}
            >
                <AppsIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.6rem' } }} />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(26, 31, 58, 0.95)'
                            : 'rgba(248, 249, 250, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        mt: 1,
                        minWidth: 200,
                        p: 2,
                    },
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: theme.palette.text.secondary,
                        mb: 2,
                        px: 1,
                        fontWeight: 600,
                    }}
                >
                    Apps
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                    }}
                >
                    {visibleApps.map((app) => (
                        <Box
                            key={app.id}
                            onClick={() => handleAppClick(app)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 1.5,
                                borderRadius: 2,
                                cursor: loadingSso && app.external ? 'wait' : 'pointer',
                                opacity: loadingSso && app.external ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    backgroundColor: app.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    mb: 1,
                                    position: 'relative',
                                }}
                            >
                                {loadingSso && app.external ? (
                                    <CircularProgress size={24} sx={{ color: 'white' }} />
                                ) : (
                                    app.icon
                                )}
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                {app.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    );
};
